import {
    CompiledObjectRules,
    EvaluatedStateObject,
    ObjectRuleSet,
    RuleNode
} from '@Core/services/proprietary/object-rule/object-rule.types';
import { UserDefinedValidation } from './page-object.types';
import {
    ComponentSetting,
    ComponentSettingLink,
    DesignerComponent,
    DesignerElement,
    EventType,
    NavigatorStyleType,
    PageSection,
    PageView,
    SectionLayoutType
} from './page.state';

export class HighlightErrors {
    static readonly type = '[Page] Highlight Errors';
    constructor() { }
}

export class ResetErrors {
    static readonly type = '[Page] Reset Errors';
    constructor() { }
}

export class SetSectionVisible {
    static readonly type = '[Page] Set Section Visible';
    constructor(public sectionID: number, public visible: boolean) { }
}

export class SetAllSectionsVisible {
    static readonly type = '[Page] Set All Sections Visible';
    constructor() { }
}

export class SetSingleSectionMode {
    static readonly type = '[Page] Set Single Section Mode';
    constructor(public on: boolean) { }
}

export class SetReadOnlyMode {
    static readonly type = '[Page] Set ReadOnly Mode';
    constructor(public on: boolean) { }
}

export class SetDesignerMode {
    static readonly type = '[Page] Set Designer Mode';
    constructor(public on: boolean) { }
}

export class SetProtectedMode {
    static readonly type = '[Page] Set Protected Mode';
    constructor(public on: boolean) { }
}

export class SetNavigatorStyle {
    static readonly type = '[Page] Set Navigator Style';
    constructor(public navigatorStyle: NavigatorStyleType) { }
}

export class SetManualNavigationMode {
    static readonly type = '[Page] Set Manual Navigation Mode';
    constructor(public on: boolean) { }
}

export class SetCurrentSection {
    static readonly type = '[Page] Set Current Section';
    constructor(public sectionID: number = null) { }
}

export class SetCurrentSectionByTitle {
    static readonly type = '[Page] Set Current Section By Title';
    constructor(public title: string = null) { }
}

export class SelectSection {
    static readonly type = '[Page] Select Section';
    constructor(public sectionID: number = null) { }
}

export class SetSectionLayout {
    static readonly type = '[Page] Set Section Layout';
    constructor(public sectionID: number, public Layout: SectionLayoutType) { }
}

export class SetAllSectionsReadOnly {
    static readonly type = '[Page] Set All Sections ReadOnly';
    constructor(public readOnly: boolean, public excludingSections: string[] = null) { }
    //Note:  Excluding sections is a way to exclude a list of sections from the readonly setting
    //       can be an array of SectionIDs, ComponentTypes, or Section Titles
}

export class AddSections {
    static readonly type = '[Page] Add Sections';
    constructor(
        public sections: PageSection[],
        public overwrite: boolean = true,
        public insertAfterSectionID: number = null,
        public ParentSectionID: number = null
    ) { }
}

export class ReplaceChildSections {
    static readonly type = '[Page] Replace Child Sections';
    constructor(public parentSectionID: number, public sections: PageSection[]) { }
}

export class ExpandCollapseChildSections {
    static readonly type = '[Page] Expand Collapse Child Sections';
    constructor(public sectionID: number) { }
}

export class DeleteSection {
    static readonly type = '[Page] Delete Section';
    constructor(public SectionID: number) { }
}

export class AddPageViews {
    static readonly type = '[Page] Add Page Views';
    constructor(
        public pageViews: PageView[],
        public autoAddSections: boolean = true,
        public defaultPageLayoutCode: string = null
    ) { }
}

export class SetCurrentPageView {
    static readonly type = '[Page] Set Current Page View';
    constructor(public viewID: number) { }
}

export class SetPageViewSections {
    static readonly type = '[Page] Set Page View Sections';
    constructor(public sections: PageSection[]) { }
}

export class SetCoreSections {
    static readonly type = '[Page] Set Core Sections';
    constructor(public coreSections: PageSection[]) { }
}

export class SetDirty {
    static readonly type = '[Page] Set Dirty';
    constructor() { }
}

export class SetPristine {
    static readonly type = '[Page] Set Pristine';
    constructor() { }
}

export class UpdateSectionData {
    static readonly type = '[Page] Update Section Data';
    constructor(public sectionKey: any, public data: any) { }
}

export class AddValidEvent {
    static readonly type = '[Page] Add ValidEvent';
    constructor(public event: EventType) { }
}

export class SetValidEvents {
    static readonly type = '[Page] Set ValidEvents';
    constructor(
        public events: EventType[],
        public saveValidationEvent: EventType,
        public anticipatedValidationEvent: EventType
    ) { }
}

export class SetAnticipatedValidationEvent {
    static readonly type = '[Page] Set Anticipated Validation Event';
    constructor(public anticipatedValidationEvent: EventType) { }
}

export class SetPermittedEvents {
    static readonly type = '[Page] Set Permitted Events';
    constructor(public events: EventType[]) { }
}

export class SetMandatoryEvents {
    static readonly type = '[Page] Set Mandatory Events';
    constructor(public events: EventType[]) { }
}

export class SetMissingEvents {
    static readonly type = '[Page] Set Missing Events';
    constructor(public events: string[]) { }
}

export class ResetState {
    static readonly type = '[Page] Reset State';
    constructor() { }
}

export class AddObjectRuleSets {
    static readonly type = '[Page] Add Object Rule Sets';
    constructor(
        public objectRuleSets: ObjectRuleSet[],
        public overwrite: boolean = true,
        public addToCurrentPageView: boolean = false
    ) { }
}

export class ResetObjectRules {
    static readonly type = '[Page] Reset Object Rules';
    constructor() { }
}

export class RefreshObjectRules {
    static readonly type = '[Page] Refresh Object Rules';
    constructor() { }
}

export class SetCompiledRules {
    static readonly type = '[Page] Set Compiled Rules';
    constructor(public compiledObjectRules: CompiledObjectRules) { }
}

export class SetEvaluatedStateObjects {
    static readonly type = '[Page] Set Evaluated State Objects';
    constructor(public evaluateStateObjects: EvaluatedStateObject[], public event: EventType) { }
}

export class SetObjectValidationEvents {
    static readonly type = '[Page] Set Object Validation Events';
    constructor(public objectValidationEvents: EventType[]) { }
}

export class SaveValidationRules {
    static readonly type = '[Page] Save Validation Rules';
    constructor() { }
}

export class RestoreValidationRules {
    static readonly type = '[Page] Restore Validation Rules';
    constructor() { }
}

export class UpdatePageModel {
    static readonly type = '[Page] Update Page Model';
    constructor(public pageModel: any, public mergeValues: boolean = true) { }
}

export class ResetPageModel {
    static readonly type = '[Page] Reset Page Model';
    constructor() { }
}

export class TrackUnsavedChanges {
    static readonly type = '[Page] Track Unsaved Changes';
    constructor(public on: boolean = true) { }
}

export class SetLock {
    static readonly type = '[Page] Set Lock';
    constructor(public lock: any = {}) { }
}

export class ResetLock {
    static readonly type = '[Page] Reset Lock';
    constructor() { }
}

export class UpdateSectionTitle {
    static readonly type = '[Page] Update Section Title';
    constructor(public sectionID: number, public title: any) { }
}

export class UpdateSectionUxConfig {
    static readonly type = '[Page] Update Section UX Config';
    constructor(public sectionID: number, public uxConfig: any, public refeshRuleSets: boolean = false) { }
}

export class UpdateSectionUxConfigComponentSettings {
    static readonly type = '[Page] Update Section UX Config Component Setting';
    constructor(
        public sectionID: number,
        public componentName: string,
        public settingNode: string,
        public settings: any
    ) { }
}

export class UpdateSectionComponentSettingLink {
    static readonly type = '[Page] Update Section Component Setting Link';
    constructor(
        public sectionID: number,
        public componentSettingLink: ComponentSettingLink,
        public componentSetting: ComponentSetting
    ) { }
}

export class UpdateComponentSetting {
    static readonly type = '[Page] Update Component Setting';
    constructor(public componentSetting: ComponentSetting, public forcedEditStatus: string = null) { }
}

export class SetCurrentDesignerComponent {
    static readonly type = '[Page] Set Current Designer Component';
    constructor(public designerComponent: DesignerComponent) { }
}

export class SetCurrentDesignerComponentEditMode {
    static readonly type = '[Page] Set Current Designer Component Edit Mode';
    constructor(public editMode: boolean) { }
}

export class SetCurrentDesignerElement {
    static readonly type = '[Page] Set Current Designer Element';
    constructor(public element: DesignerElement) { }
}

export class AddDesignerElementRule {
    static readonly type = '[Page] Add Element Rule';
    constructor(
        public designerComponent: DesignerComponent,
        public designerElement: DesignerElement,
        public ruleNode: RuleNode
    ) { }
}

export class UpdateDesignerElementRule {
    static readonly type = '[Page] Update Element Rule';
    constructor(
        public designerComponent: DesignerComponent,
        public designerElement: DesignerElement,
        public ruleNode: RuleNode
    ) { }
}

export class DeleteDesignerElementRule {
    static readonly type = '[Page] Delete Element Rule';
    constructor(
        public desingnerComponent: DesignerComponent,
        public designerElement: DesignerElement,
        public ruleNode: RuleNode
    ) { }
}

export class SetPendingDesignerRuleChange {
    static readonly type = '[Page] Set Pending Designer Rule Change';
    constructor(public on: boolean = true) { }
}

export class UpdateSectionProperties {
    static readonly type = '[Page] Update Section Properties';
    constructor(public section: PageSection) { }
}

export class AddToComponentSettingList {
    static readonly type = '[Page] Add To Component Settings List';
    constructor(public componentSettings: any[], public preserveNames: boolean = false) { }
}

export class SetUserDefinedValidations {
    static readonly type = '[Page] Set UserDefined Validations';
    constructor(public validations: UserDefinedValidation[]) { }
}

// **** Saved Prototype Code - Will possibly be used when component templates are implemented  ****
// export class AddComponentSetting {
//   static readonly type = '[Page] Add Component Setting';
//   constructor(public setting: ComponentSetting) {}
// }

// export class UpdateComponentSetting {
//   static readonly type = '[Page] Update Component Setting';
//   constructor(public setting: ComponentSetting) {}
// }
// **************************************************************************************************
